// Angular imports
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

// CC-Common imports
import { IconConstants, Utils } from 'cc-common';

// Third party imports
import { Subscription } from 'rxjs';

// devextreme imports
import notify from 'devextreme/ui/notify';

// Service imports
import { ARAdjustmentService } from '../../_services/account-receivable/ar-adjustment.service';
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { PrecisionService } from '../../../../_services/shared-services/precision.service';
import { HelperService } from '../../../../_services/shared-services/helper.service';
import { ARCreditApplicationService } from '../../_services/account-receivable/ar-credit-application.service';
import { DetailPopupsService } from '../../_services/detail-popups.service';
import { UserService } from '../../../../_services/data-services/user.service';

// Constant imports
import { Constants } from '../../../../_constants/constants';
import { StringFormatConstants } from '../../../../_constants/string-format-constants';
import { MessageInterval } from '../../../../_constants/messages-intervals';
import { ARConstants } from '../../../cirabooks-module/_constants/cb-ar-constants';

// Model imports
import { AttachmentRawDto } from '../../../../management-common/_models/attachment-raw-dto';
import { ARAdjustmentParameterDto } from '../../_models/account-receivable/ar-adjustment/ar-adjustment-parameter-dto';
import { ARAdjustmentResultDto } from '../../_models/account-receivable/ar-adjustment/ar-adjustment-result-dto';
import { TransactionLogResultDto } from '../../_models/common/transaction-log-result-dto';
import { ARAdjustmentItemResultDto } from '../../_models/account-receivable/ar-adjustment/ar-adjustment-item-result-dto';
import { TransactionLogParameterDto } from '../../_models/common/transaction-log-parameter-dto';
import { ARCreditApplicationsResultDto } from '../../_models/account-receivable/credit-applications/ar-credit-applications-result-dto';
import { ARAdjustmentAttachmentRawDto } from '../../_models/account-receivable/ar-adjustment/ar-adjustment-attachment-raw-dto';
import { ARAdjustmentAttachmentUpdateParameterDto } from '../../_models/account-receivable/ar-adjustment/ar-adjustment-attachment-update-parameter-dto';

@Component({
    selector: 'app-ar-adjustment',
    templateUrl: './ar-adjustment.component.html',
    styleUrls: ['./ar-adjustment.component.scss']
})
export class ARAdjustmentComponent implements OnInit, OnDestroy {
    @Input() visible: boolean;
    @Input() adjustmentDetailParam: ARAdjustmentParameterDto;
    @Output() onAdjustmentDetailClose = new EventEmitter<boolean>();

    popUpTitle: string;
    loadingVisible: boolean;
    hasOtherDimensions: boolean;
    currencyFormat: any;
    dateFormat: any;
    adjustmentDetail: ARAdjustmentResultDto;
    transactionLogResultData: TransactionLogResultDto[] = [];

    displayTransactionLogPopUp = false;
    transactionLogTitle: string;
    defaultLabelLocation: string;

    showCreditApplication = false;
    transactionType = 'Adjustment';
    creditApplicationData: ARCreditApplicationsResultDto[] = [];
    attachmentsData: AttachmentRawDto[];

    showManualCreditApplication = false;
    showReverseEditor = false;
    showWriteOffEditor = false;
    showARAdjustmentEditor = false;

    arAdjustmentItems: ARAdjustmentItemResultDto[] = [];
    arAdjustmentEditorTitle: string;

    isLimitedEdit = false;
    isInternalUser = false;

    iconCancel = IconConstants.CANCEL;
    iconEdit = IconConstants.EDIT;
    iconDelete = IconConstants.DELETE;
    iconReverse = IconConstants.REVERSE;
    iconWriteOff = IconConstants.WRITE_OFF;
    iconHistory = IconConstants.HISTORY;
    iconAttachment = IconConstants.ATTACHMENT;

    forceReloadSubscription: Subscription;

    constructor(
        private readonly arAdjustmentService: ARAdjustmentService,
        private readonly serverErrorService: ServerErrorService,
        private readonly precisionService: PrecisionService,
        private readonly helperService: HelperService,
        private readonly arCreditApplicationService: ARCreditApplicationService,
        private readonly detailPopupsService: DetailPopupsService,
        private readonly userService: UserService
    ) {
        this.isInternalUser = this.userService.isInternal();

        this.currencyFormat = this.precisionService.updatePrecisionPoint(StringFormatConstants.currencyFormat);

        this.calculateCustomSummary = this.calculateCustomSummary.bind(this);
        this.isAdjustmentTypeCredit = this.isAdjustmentTypeCredit.bind(this);
    }

    ngOnInit() {
        this.defaultLabelLocation = Constants.defaultFormLabelLocation;
        this.dateFormat = StringFormatConstants.dateFormat;
        if (Utils.notNullAndDefined(this.adjustmentDetailParam)) {
            this.popUpTitle = `AR Adjustment ${this.adjustmentDetailParam.adjustmentId}`;
            this.populateAdjustmentDetail(false);
        }

        this.forceReloadSubscription = this.detailPopupsService.onForceReload().subscribe(() => {
            this.populateAdjustmentDetail(true);
        });
    }

    ngOnDestroy() {
        if (Utils.notNullAndDefined(this.forceReloadSubscription)) {
            this.forceReloadSubscription.unsubscribe();
        }
    }

    onHidden(e): void {
        this.visible = false;
        this.popUpTitle = '';
        this.adjustmentDetailParam = null;
        this.onAdjustmentDetailClose.emit(false);
    }

    onCustomerNameClick(data): void {
        if (Utils.notNullAndDefined(this.adjustmentDetailParam.communityId)) {
            this.helperService.openDefaultPropertyPageInNewWindow(
                this.adjustmentDetailParam.communityId,
                data.customerCode
            );
        }
    }

    onTransactionLogClick(): void {
        if (Utils.notNullAndDefined(this.adjustmentDetailParam.adjustmentId)) {
            this.populateTransactionLog();
            this.transactionLogTitle = `Log of AR Adjustment ${this.adjustmentDetailParam.adjustmentId}`;
            this.displayTransactionLogPopUp = true;
        }
    }

    onTransactionLogClose(e): void {
        this.displayTransactionLogPopUp = false;
    }

    isNotRequiredDimension(dim): boolean {
        return Utils.isNullOrUndefined(
            this.adjustmentDetail.requiredDimensions.find((rdim) => Utils.equalsIgnoreCase(rdim.key, dim.dimensionCode))
        );
    }

    onSaveAttachments(attachments: any[]): void {
        const arAdjustmentAttachments = attachments
            .filter((attachment) => Utils.notNullOrEmpty(attachment.action))
            .map((attachment) => {
                const obj: ARAdjustmentAttachmentRawDto = new ARAdjustmentAttachmentRawDto();
                obj.fileId = attachment.fileId;
                obj.fileName = attachment.fileName;
                obj.crudAction = attachment.action;
                obj.fileDataBase64 = attachment.fileBase64String;
                return obj;
            });
        if (Utils.notNullOrEmpty(arAdjustmentAttachments)) {
            const baseParam = new ARAdjustmentAttachmentUpdateParameterDto();
            baseParam.managementCompanyId = this.adjustmentDetailParam.managementCompanyId;
            baseParam.communityId = this.adjustmentDetailParam.communityId;
            baseParam.arAdjustmentId = this.adjustmentDetail.arAdjustmentId;
            baseParam.attachments = arAdjustmentAttachments;
            this.loadingVisible = true;
            this.arAdjustmentService.uploadAttachments(baseParam).subscribe(
                () => {
                    this.loadingVisible = false;
                    this.populateAdjustmentDetail(true);
                },
                (error) => {
                    this.loadingVisible = false;
                    this.serverErrorService.displayError(error);
                    this.populateAdjustmentDetail(true);
                }
            );
        }
    }

    calculateCustomSummary(e): void {
        this.helperService.calculateTotalSummary(e);
    }

    closeCreditApplication(refresh: boolean): void {
        this.showCreditApplication = false;
        if (Utils.isTrue(refresh)) {
            this.populateAdjustmentDetail(true);
        }
    }

    onDeleteClick(data: ARAdjustmentResultDto): void {
        const dialog = this.helperService.getConfirmationDialog(
            'Confirmation',
            '<b> Are you sure you want to delete this adjustment ?</b>'
        );
        dialog.show().then((answer: boolean) => {
            if (Utils.isTrue(answer)) {
                this.deleteAdjustment(data);
            }
        });
    }

    onReverseClick(): void {
        this.showReverseEditor = true;
    }

    onWriteOffClick(): void {
        const dialog = this.helperService.getConfirmationDialog(
            'Confirmation',
            '<b> Are you sure you want to write-off this adjustment ?</b>'
        );
        dialog.show().then((answer: boolean) => {
            if (Utils.isTrue(answer)) {
                this.showWriteOffEditor = true;
            }
        });
    }

    onWriteOffEditorClose(refresh: boolean): void {
        this.showWriteOffEditor = false;
        if (Utils.isTrue(refresh)) {
            this.populateAdjustmentDetail(true);
        }
    }

    onReverseEditorClose(refresh: boolean): void {
        this.showReverseEditor = false;
        if (Utils.isTrue(refresh)) {
            this.populateAdjustmentDetail(true);
        }
    }

    onBasicEditClick(): void {
        this.isLimitedEdit = true;
        this.arAdjustmentEditorTitle = `AR Adjustment ${this.adjustmentDetailParam.adjustmentId}`;
        this.showARAdjustmentEditor = true;
    }

    onEditClick(): void {
        this.isLimitedEdit = false;
        this.arAdjustmentEditorTitle = `AR Adjustment ${this.adjustmentDetailParam.adjustmentId}`;
        this.showARAdjustmentEditor = true;
    }

    onAdjustmentEditorClose(refresh: boolean): void {
        this.isLimitedEdit = false;
        this.showARAdjustmentEditor = false;

        if (Utils.isTrue(refresh)) {
            this.populateAdjustmentDetail(true);
        }
    }

    onPaymentApplicationClick(): void {
        this.loadingVisible = true;
        this.arCreditApplicationService.getARAdjustmentCreditApplications(this.adjustmentDetailParam).subscribe(
            (result) => {
                this.creditApplicationData = result;
                this.showCreditApplication = true;
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.serverErrorService.displayError(error);
            }
        );
    }

    getHtmlNote(data: ARAdjustmentResultDto): any {
        let message = '';
        if (Utils.notNullAndDefined(data)) {
            const idxOfSecondPipe = data.messageDetails.indexOf('|', data.messageDetails.indexOf('|') + 1);
            const referanceNumber = data.messageDetails.slice(idxOfSecondPipe + 1);

            let hyperLinkNumber = '';

            if (Utils.isTrue(this.isInternalUser)) {
                hyperLinkNumber = `<a class="cc-hyper-link">${referanceNumber}</a>`;
            } else {
                // If non internal user, then convert it to simple text
                hyperLinkNumber = `<span class="cc-hyper-link">${referanceNumber}</span>`;
            }
            message = data.message.replace(referanceNumber, hyperLinkNumber);
        }
        return message;
    }

    onMessageDetailClick(data: ARAdjustmentResultDto): void {
        if (Utils.notNullAndDefined(data) && Utils.isTrue(this.isInternalUser)) {
            const headerId = data.referenceId;
            this.detailPopupsService.showDetailsPopup('AR', data.referenceType, {
                managementCompanyId: this.adjustmentDetailParam.managementCompanyId,
                communityId: this.adjustmentDetailParam.communityId,
                headerId
            });
        }
    }

    showARManualCreditApplication(): void {
        this.showManualCreditApplication = true;
    }

    onManualCAEditorClose(refresh: boolean): void {
        this.showManualCreditApplication = false;
        if (Utils.isTrue(refresh)) {
            this.populateAdjustmentDetail(true);
        }
    }

    isAdjustmentTypeCredit(): boolean {
        return Utils.equalsIgnoreCase(this.adjustmentDetail.debitOrCredit, ARConstants.ADJUSTMENT_TYPE_CREDIT);
    }

    private deleteAdjustment(data: ARAdjustmentResultDto): void {
        const param: ARAdjustmentParameterDto = new ARAdjustmentParameterDto();
        param.adjustmentId = data.arAdjustmentId;
        param.managementCompanyId = this.adjustmentDetailParam.managementCompanyId;
        param.communityId = this.adjustmentDetailParam.communityId;
        param.clearCache = this.adjustmentDetailParam.clearCache;
        this.loadingVisible = true;
        this.arAdjustmentService.deleteARAdjustment(param).subscribe(
            () => {
                notify('Adjustment deleted successfully.', 'success', MessageInterval.successMsgInterval);
                this.visible = false;
                this.onAdjustmentDetailClose.emit(true);
                this.loadingVisible = false;
            },
            (error) => {
                this.serverErrorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private populateAdjustmentDetail(clearCache: boolean): void {
        this.loadingVisible = true;
        this.adjustmentDetailParam.clearCache = clearCache;
        this.arAdjustmentService.getARAdjustmentDetail(this.adjustmentDetailParam).subscribe(
            (result: ARAdjustmentResultDto) => {
                this.loadingVisible = false;
                result.items.forEach((item) => {
                    item.dimensions.forEach((dim) => {
                        const reqDim = result.requiredDimensions.find((rdim) =>
                            Utils.equalsIgnoreCase(rdim.key, dim.dimensionCode)
                        );
                        if (Utils.notNullAndDefined(reqDim)) {
                            item[dim.dimensionCode] = dim.dimensionOptionTitle;
                        } else {
                            this.hasOtherDimensions = true;
                        }
                    });
                });
                this.adjustmentDetail = result;
                this.arAdjustmentItems = result.items;
                if (Utils.notNullOrEmpty(result.attachments)) {
                    this.attachmentsData = result.attachments.map((r) => {
                        const rec = new AttachmentRawDto();
                        rec.fileId = r.fileId;
                        rec.fileName = r.fileName;
                        rec.fileUrl = r.downloadUrl;
                        return rec;
                    });
                } else {
                    this.attachmentsData = [];
                }
            },
            (error) => {
                this.loadingVisible = false;
                this.serverErrorService.displayError(error);
            }
        );
    }

    private populateTransactionLog(): void {
        const baseParam: TransactionLogParameterDto = new TransactionLogParameterDto();
        baseParam.transactionId = this.adjustmentDetailParam.adjustmentId;
        baseParam.clearCache = true;
        this.loadingVisible = true;
        this.arAdjustmentService.getARAdjustmentHistory(baseParam).subscribe(
            (result: TransactionLogResultDto[]) => {
                this.transactionLogResultData = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.serverErrorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }
}
